import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Save, Brain, Scale, BarChart, TrendingUp, Key } from 'lucide-react';
import toast from 'react-hot-toast';

function AISettings() {
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openaiKey, setOpenaiKey] = useState('');
  const [aixScores, setAixScores] = useState({
    shortTerm: 0,
    midTerm: 0,
    longTerm: 0
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('ai_settings, openai_api_key')
        .eq('id', 1)
        .single();

      if (error) throw error;
      
      setSettings(data?.ai_settings || null);
      setOpenaiKey(data?.openai_api_key || '');
      
      // Calculate AIX scores based on settings
      if (data?.ai_settings) {
        calculateAIXScores(data.ai_settings);
      }
    } catch (error) {
      console.error('Error loading AI settings:', error);
      toast.error('Failed to load AI settings');
    } finally {
      setLoading(false);
    }
  };

  const calculateAIXScores = (settings: any) => {
    // Calculate scores based on weighted metrics
    const weights = settings.analysis_weights;
    
    // Calculate base score from weighted metrics
    const getBaseScore = () => {
      let score = 0;
      Object.entries(weights).forEach(([category, weight]: [string, any]) => {
        if (settings[category]?.enabled) {
          const metrics = settings[category].metrics;
          if (metrics) {
            const categoryScore = Object.values(metrics).reduce((sum: any, val: any) => sum + val, 0) / Object.keys(metrics).length;
            score += (categoryScore * weight) / 100;
          }
        }
      });
      return score;
    };

    const baseScore = getBaseScore();
    
    // Convert to -100 to 100 scale with time-based adjustments
    setAixScores({
      shortTerm: Math.round(baseScore * 2 - 100),
      midTerm: Math.round((baseScore * 0.9) * 2 - 100), // Slightly reduced confidence for mid-term
      longTerm: Math.round((baseScore * 0.8) * 2 - 100)  // Further reduced for long-term
    });
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('admin_settings')
        .update({ 
          ai_settings: settings,
          openai_api_key: openaiKey,
          updated_at: new Date().toISOString()
        })
        .eq('id', 1);

      if (error) throw error;
      toast.success('AI settings saved successfully');
      calculateAIXScores(settings);
    } catch (error) {
      console.error('Error saving AI settings:', error);
      toast.error('Failed to save AI settings');
    } finally {
      setSaving(false);
    }
  };

  const handleMetricChange = (category: string, metric: string, value: number) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        metrics: {
          ...prev[category].metrics,
          [metric]: Math.max(0, Math.min(100, value))
        }
      }
    }));
  };

  const handleWeightChange = (category: string, value: number) => {
    setSettings(prev => {
      const weights = {
        ...prev.analysis_weights,
        [category]: Math.max(0, Math.min(100, value))
      };
      
      // Normalize weights to sum to 100
      const total = Object.values(weights).reduce((sum: any, val: any) => sum + val, 0);
      const normalizedWeights = Object.fromEntries(
        Object.entries(weights).map(([key, val]) => [
          key,
          Math.round((val as number / total) * 100)
        ])
      );

      return {
        ...prev,
        analysis_weights: normalizedWeights
      };
    });
  };

  const renderAIXScoreBar = (score: number, label: string) => (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-sm text-gray-300">{label}</span>
        <span className={`text-lg font-bold ${
          score > 0 ? 'text-green-400' : score < 0 ? 'text-red-400' : 'text-gray-400'
        }`}>
          {score > 0 ? '+' : ''}{score}
        </span>
      </div>
      <div className="relative h-4 rounded-full overflow-hidden bg-gradient-to-r from-red-500 via-yellow-500 to-green-500">
        <div 
          className="absolute top-0 bottom-0 left-1/2 w-px bg-white"
          style={{ transform: 'translateX(-50%)' }}
        />
        <div
          className="absolute top-0 bottom-0 bg-black/50"
          style={{ 
            left: `${((score + 100) / 2)}%`,
            width: '4px',
            transform: 'translateX(-50%)'
          }}
        />
      </div>
      <div className="flex justify-between text-xs">
        <span className="text-red-400">Sell 100%</span>
        <span className="text-white">0%</span>
        <span className="text-green-400">Buy 100%</span>
      </div>
    </div>
  );

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* OpenAI API Key Section */}
      <div className="bg-blue-900/40 backdrop-blur-sm rounded-lg p-6 border border-blue-500/20">
        <div className="flex items-center space-x-3 mb-4">
          <Key className="h-6 w-6 text-cyan-400" />
          <h3 className="text-xl font-semibold text-white">OpenAI API Key</h3>
        </div>
        <div className="space-y-4">
          <div>
            <input
              type="password"
              value={openaiKey}
              onChange={(e) => setOpenaiKey(e.target.value)}
              className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
              placeholder="Enter OpenAI API key"
            />
            <p className="mt-1 text-sm text-gray-400">
              Required for AI market analysis. Keep it secure and never share it.
            </p>
          </div>
        </div>
      </div>

      {/* AIX Scores */}
      <div className="bg-gradient-to-r from-blue-900/40 to-cyan-900/40 backdrop-blur-sm rounded-lg p-6 border border-cyan-500/20">
        <div className="flex items-center space-x-3 mb-6">
          <TrendingUp className="h-8 w-8 text-cyan-400" />
          <div>
            <h2 className="text-2xl font-bold text-white">AIX Score</h2>
            <p className="text-gray-300">Market Position Indicator</p>
          </div>
        </div>
        
        <div className="space-y-6">
          {renderAIXScoreBar(aixScores.shortTerm, 'Short Term (24h)')}
          {renderAIXScoreBar(aixScores.midTerm, 'Mid Term (7d)')}
          {renderAIXScoreBar(aixScores.longTerm, 'Long Term (30d)')}
        </div>
      </div>

      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
          <Brain className="h-6 w-6" />
          <span>AI Analysis Settings</span>
        </h2>
        <button
          onClick={handleSaveSettings}
          disabled={saving}
          className="flex items-center space-x-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md transition-colors"
        >
          <Save className="h-5 w-5" />
          <span>{saving ? 'Saving...' : 'Save Settings'}</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Market Settings */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-white flex items-center space-x-2">
            <BarChart className="h-5 w-5" />
            <span>Market Settings</span>
          </h3>

          <div className="bg-blue-900/40 backdrop-blur-sm rounded-lg p-6 border border-blue-500/20">
            <div className="space-y-4">
              {Object.entries(settings.historical_data.indicators).map(([key, value]: [string, any]) => (
                <div key={key} className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-200 capitalize">
                    {key.toUpperCase()}
                  </label>
                  <button
                    onClick={() => handleMetricChange('historical_data', key, !value)}
                    className={`px-4 py-2 rounded-md transition-colors ${
                      value 
                        ? 'bg-cyan-500 text-white' 
                        : 'bg-blue-900/40 text-gray-400 hover:text-white'
                    }`}
                  >
                    {value ? 'Enabled' : 'Disabled'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Private Board Settings */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-white flex items-center space-x-2">
            <BarChart className="h-5 w-5" />
            <span>Private Board Settings</span>
          </h3>

          <div className="bg-blue-900/40 backdrop-blur-sm rounded-lg p-6 border border-blue-500/20">
            <div className="space-y-4">
              {Object.entries(settings.private_board.metrics).map(([key, value]: [string, any]) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-200 mb-2 capitalize">
                    {key.replace(/_/g, ' ')}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={value}
                    onChange={(e) => handleMetricChange('private_board', key, parseInt(e.target.value))}
                    className="w-full h-2 bg-orange-500/20 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>0%</span>
                    <span>{value}%</span>
                    <span>100%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Weight Settings */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-white flex items-center space-x-2">
            <Scale className="h-5 w-5" />
            <span>Weight Settings</span>
          </h3>

          <div className="bg-blue-900/40 backdrop-blur-sm rounded-lg p-6 border border-blue-500/20">
            <div className="space-y-4">
              {Object.entries(settings.analysis_weights).map(([key, value]: [string, any]) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-200 mb-2 capitalize">
                    {key.replace(/_/g, ' ')}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={value}
                    onChange={(e) => handleWeightChange(key, parseInt(e.target.value))}
                    className="w-full h-2 bg-orange-500/20 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>0%</span>
                    <span>{value}%</span>
                    <span>100%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <style>{`
        /* Lighter orange track */
        input[type="range"] {
          background: rgba(251, 146, 60, 0.15);
        }

        /* White dot */
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          border: 2px solid rgba(251, 146, 60, 0.5);
        }

        input[type="range"]::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          border: 2px solid rgba(251, 146, 60, 0.5);
        }
      `}</style>
    </div>
  );
}

export default AISettings;
